import React, {
  useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { useFetchye } from 'fetchye';
import { Pagination, ProgressCircular } from '@americanexpress/dls-react';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';
import DocList from './DocList';
import styles from './styles.css';

function AxpDocumentUploadHistory(props) {
  const {
    filePreview,
    downloadProgress,
    altDocIdProp,
    processId,
    consumerId,
    guid,
  } = props;
  const [page, setPage] = useState(1);
  const inboundURL = useSelector((state) => state.getIn(['config', 'imageViewerDocUploadApiUrl']));
  const dispatch = useDispatch();
  const locale = useSelector((state) => state.getIn(['intl', 'activeLocale']));
  const languageDataImm = useSelector(
    (state) => state.getIn(['intl', 'languagePacks', locale, 'axp-document-upload-history', 'data'])
  );
  const languageData = languageDataImm ? languageDataImm.toJS() : {};

  useEffect(() => {
    dispatch(loadLanguagePack('axp-document-upload-history', { fallbackLocale: 'en-US' }));
  }, [locale, dispatch]);

  const requestBody = {
    processId,
    consumerId,
    altDocId: [altDocIdProp],
  };

  const { isLoading, data } = useFetchye(`${inboundURL}/servicing/v1/communications/uploadHistory`, {
    defer: !inboundURL,
    credentials: 'include',
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'icaas-correlation-id': `${guid}`,
    },
    body: JSON.stringify(requestBody),
  });

  const { historyData, formattedList } = useMemo(() => {
    if (data?.body?.status?.statusCode === '55200' && data?.body?.response) {
      const historyDataArr = Object.values(data.body.response);
      const newList = [];
      if (historyDataArr.length > 0 && historyDataArr[0] !== null) {
        const arrayCopy = historyDataArr.map((arr) => (arr ? [...arr] : arr));
        while (arrayCopy[0] !== null && arrayCopy[0].length > 0) {
          newList.push(arrayCopy[0].splice(0, 10));
        }
      }
      return { historyData: historyDataArr, formattedList: newList };
    }
    return { historyData: [], formattedList: [] };
  }, [data]);

  const handlePage = (pageNumber) => {
    setPage(pageNumber);
  };

  if (Object.keys(languageData).length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 0' }}>
        <ProgressCircular size="md" />
      </div>
    );
  }

  return (
    <main>
      <IntlProvider locale={locale} messages={languageData}>
        <div className={styles.container}>
          <h2 className={styles.heading}>
            <FormattedMessage id="mainHeading" />
          </h2>
          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
              <ProgressCircular size="md" />
            </div>
          )}
          {historyData.length > 0 && historyData[0] !== null ? (
            <div>
              <p className={styles.subtitle}>
                <FormattedMessage id="historyInfo" />
              </p>
              <DocList
                retrievedDocs={formattedList[page - 1]}
                filePreview={filePreview}
                downloadProgress={downloadProgress}
                locale={locale}
              />
              {formattedList.length > 1
                && (
                <Pagination
                  id="historyPagination"
                  selectedPage={page}
                  totalResults={historyData[0].length}
                  showResultsPerPage={false}
                  onChange={(pageNumber) => handlePage(pageNumber)}
                />
                )}
            </div>
          )
            : !isLoading && (
              <p className={styles.subtitle}>
                <FormattedMessage id="noHistory" />
              </p>
            )}
        </div>
      </IntlProvider>
    </main>
  );
}

AxpDocumentUploadHistory.defaultProps = {
  altDocIdProp: '',
  filePreview: null,
  downloadProgress: null,
  guid: uuidV4(),
};

AxpDocumentUploadHistory.propTypes = {
  altDocIdProp: PropTypes.string,
  filePreview: PropTypes.func,
  downloadProgress: PropTypes.instanceOf(Object),
  consumerId: PropTypes.string.isRequired,
  processId: PropTypes.string.isRequired,
  guid: PropTypes.string,
};

export const loadModuleData = async ({ store: { dispatch } }) => {
  await dispatch(loadLanguagePack('axp-document-upload-history', { fallbackLocale: 'en-US' }));
};

AxpDocumentUploadHistory.holocron = {
  name: 'axp-document-upload-history',
  loadModuleData,
};

export default AxpDocumentUploadHistory;
